<template>
  <div class="w-fit">
    <div ref="activator">
      <UIButton id="language" variant="ghost" left-icon="world" @click="isMenuVisible = !isMenuVisible">
        <span class="font-bold capitalize !text-black-100">{{ locale }}</span>
      </UIButton>
    </div>

    <!-- Mobile language menu -->
    <UIBaseTransition>
      <Teleport v-if="isMobile && isMenuVisible" to="body">
        <div class="fixed inset-0 bg-black bg-opacity-50 z-[1000]" @click="isMenuVisible = false">
          <div class="fixed bottom-0 left-0 right-0 bg-white rounded-t-xl p-4" @click.stop>
            <UIMenu
              ref="menu"
              value-key="code"
              :items="locales"
              :menu-visible="true"
              :menu-width="'100%'"
              :full-width="true"
              :style="{ boxShadow: 'none', border: 'none' }"
            >
              <template #default="{ item, active }">
                <UIMenuOption :active :item @select="setLanguage">
                  <slot :active :item />
                </UIMenuOption>
              </template>
            </UIMenu>
          </div>
        </div>
      </Teleport>
    </UIBaseTransition>

    <!-- Desktop language menu -->
    <Teleport to="body">
      <UIBaseTransition>
        <UIMenu
          v-if="isMenuVisible && !isMobile"
          ref="menu"
          value-key="code"
          :items="locales"
          :menu-visible="isMenuVisible"
          :style="floatingStyles"
          menu-width="200px"
        >
          <template #default="{ item, active }">
            <UIMenuOption :active :item @select="setLanguage">
              <slot :active :item />
            </UIMenuOption>
          </template>
        </UIMenu>
      </UIBaseTransition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/vue'
import { useTolgee } from '@tolgee/vue'

const { locale, locales, setLocale } = useI18n()

const { isMobile } = useAppBreakpoints()

const menu = ref(null)
const activator = ref<HTMLDivElement | null>(null)

const isMenuVisible = ref(false)

const { floatingStyles } = useFloating(activator, menu, {
  placement: 'bottom-end',
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware: [
    flip({
      fallbackPlacements: ['top-end'],
    }),
    offset(5),
  ],
  open: isMenuVisible,
})
// Only apply onClickOutside for desktop
if (!isMobile) {
  onClickOutside(
    menu,
    () => {
      isMenuVisible.value = false
    },
    { ignore: [activator] }
  )
}

const tolgee = useTolgee(['language'])

const emits = defineEmits<{
  (e: 'languageChanged', langCode: string): void
}>()

const setLanguage = async ({ code }: { code: string }) => {
  setLocale(code)
  await tolgee.value.changeLanguage(code)
  emits('languageChanged', code)

  setTimeout(() => {
    window.location.reload()
  }, 100)
}
</script>

<style scoped lang="scss">
.fixed {
  &.bg-white {
    animation: slideUp 0.25s ease-out;
    max-height: 80vh;
    overflow-y: auto;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>
